import React, { useState } from 'react';
import { Button, Card, Stack, Flex, useTheme, spacing, fontFamily } from '@fti/design-system';
import type { BrowserQuote } from './types';

export interface QuoteCardProps {
  quote: BrowserQuote;
  rank: number;
  onToggleStar: (id: string) => void;
  onSetEdited: (id: string, text: string) => void;
  onResetEdit: (id: string) => void;
}

function scoreTone(score: number, theme: ReturnType<typeof useTheme>['theme']) {
  if (score >= 85) {
    return {
      bg: `${theme.colors.semantic.success.light}25`,
      fg: theme.colors.semantic.success.dark,
      border: `${theme.colors.semantic.success.light}80`,
    };
  }
  if (score >= 70) {
    return {
      bg: theme.colors.primary[50],
      fg: theme.colors.primary[700],
      border: theme.colors.primary[100],
    };
  }
  return {
    bg: theme.colors.background.paper,
    fg: theme.colors.text.secondary,
    border: theme.colors.border.default,
  };
}

export const QuoteCard: React.FC<QuoteCardProps> = ({
  quote,
  rank,
  onToggleStar,
  onSetEdited,
  onResetEdit,
}) => {
  const { theme } = useTheme();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(quote.editedText ?? quote.text);
  const [contextOpen, setContextOpen] = useState(false);
  const [hover, setHover] = useState(false);

  const displayText = quote.editedText ?? quote.text;
  const tone = scoreTone(quote.score, theme);

  const handleSave = () => {
    if (draft.trim() && draft !== quote.text) {
      onSetEdited(quote.id, draft);
    } else if (draft === quote.text) {
      onResetEdit(quote.id);
    }
    setEditing(false);
  };

  const handleStartEdit = () => {
    setDraft(quote.editedText ?? quote.text);
    setEditing(true);
  };

  const handleReset = () => {
    onResetEdit(quote.id);
    setDraft(quote.text);
    setEditing(false);
  };

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        borderRadius: '14px',
        background: '#FFFFFF',
        border: `1px solid ${
          quote.starred ? theme.colors.primary[400] : theme.colors.border.default
        }`,
        boxShadow: hover
          ? '0 8px 24px -10px rgba(0, 84, 163, 0.18)'
          : quote.starred
          ? `0 0 0 3px ${theme.colors.primary[50]}`
          : '0 1px 2px rgba(15, 23, 42, 0.04)',
        transition: 'box-shadow 0.18s ease, border-color 0.18s ease, transform 0.18s ease',
        transform: hover ? 'translateY(-1px)' : 'none',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Left rank bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: '4px',
          background: `linear-gradient(180deg, ${theme.colors.primary[600]}, ${theme.colors.primary[400]})`,
          opacity: quote.starred ? 1 : 0.7,
        }}
      />

      <div style={{ padding: spacing.lg, paddingLeft: `calc(${spacing.lg} + 8px)` }}>
        <Stack gap="md">
          {/* Header row */}
          <Flex justify="between" align="center" wrap="wrap" gap="sm">
            <Flex align="center" gap="sm">
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: theme.colors.primary[700],
                  color: '#FFFFFF',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  fontFamily: fontFamily.sans,
                }}
              >
                {rank}
              </span>
              <span
                style={{
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  color: tone.fg,
                  background: tone.bg,
                  border: `1px solid ${tone.border}`,
                  padding: '4px 10px',
                  borderRadius: '999px',
                  letterSpacing: '0.02em',
                }}
              >
                {quote.score}/100
              </span>
              {quote.speaker && (
                <span
                  style={{
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: theme.colors.text.primary,
                    background: theme.colors.background.paper,
                    border: `1px solid ${theme.colors.border.default}`,
                    padding: '4px 10px',
                    borderRadius: '999px',
                  }}
                >
                  {quote.speaker}
                </span>
              )}
              {quote.editedText !== null && (
                <span
                  style={{
                    fontSize: '0.72rem',
                    color: theme.colors.semantic.warning.dark,
                    fontStyle: 'italic',
                  }}
                >
                  edited
                </span>
              )}
            </Flex>
            <button
              type="button"
              onClick={() => onToggleStar(quote.id)}
              aria-label={quote.starred ? 'Unstar' : 'Star'}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1.4rem',
                color: quote.starred
                  ? theme.colors.semantic.warning.default
                  : theme.colors.text.disabled,
                transition: 'color 0.15s, transform 0.15s',
                lineHeight: 1,
                padding: 4,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.18)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {quote.starred ? '★' : '☆'}
            </button>
          </Flex>

          {/* Quote body */}
          {editing ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: spacing.md,
                fontSize: '1.05rem',
                fontFamily: fontFamily.sans,
                lineHeight: 1.55,
                borderRadius: '8px',
                border: `1px solid ${theme.colors.primary[400]}`,
                outline: 'none',
                background: '#FFFFFF',
                color: theme.colors.text.primary,
                resize: 'vertical',
                boxShadow: `0 0 0 3px ${theme.colors.primary[100]}`,
                boxSizing: 'border-box',
              }}
              autoFocus
            />
          ) : (
            <blockquote
              style={{
                margin: 0,
                fontFamily: fontFamily.sans,
                fontSize: '1.15rem',
                lineHeight: 1.5,
                fontWeight: 500,
                color: theme.colors.text.primary,
                letterSpacing: '-0.005em',
              }}
            >
              <span
                aria-hidden
                style={{
                  fontFamily: 'Georgia, serif',
                  fontSize: '1.8em',
                  lineHeight: 0,
                  verticalAlign: '-0.35em',
                  color: theme.colors.primary[300],
                  marginRight: '0.1em',
                }}
              >
                “
              </span>
              {displayText}
              <span
                aria-hidden
                style={{
                  fontFamily: 'Georgia, serif',
                  fontSize: '1.8em',
                  lineHeight: 0,
                  verticalAlign: '-0.45em',
                  color: theme.colors.primary[300],
                  marginLeft: '0.05em',
                }}
              >
                ”
              </span>
            </blockquote>
          )}

          {/* Rationale */}
          <p
            style={{
              margin: 0,
              fontSize: '0.875rem',
              color: theme.colors.text.secondary,
              lineHeight: 1.5,
            }}
          >
            {quote.rationale}
          </p>

          {/* Actions */}
          <Flex gap="sm" align="center" wrap="wrap">
            {editing ? (
              <>
                <Button variant="solid" color="primary" size="sm" onClick={handleSave}>
                  Save
                </Button>
                <Button
                  variant="outline"
                  color="primary"
                  size="sm"
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" color="primary" size="sm" onClick={handleStartEdit}>
                  Edit
                </Button>
                {quote.editedText !== null && (
                  <Button variant="outline" color="error" size="sm" onClick={handleReset}>
                    Reset
                  </Button>
                )}
              </>
            )}
            <button
              type="button"
              onClick={() => setContextOpen((v) => !v)}
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.colors.primary[600],
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: 'pointer',
                padding: 0,
                fontFamily: fontFamily.sans,
              }}
            >
              {contextOpen ? '− Hide context' : '+ Show context'}
            </button>
          </Flex>

          {contextOpen && (
            <div
              style={{
                fontSize: '0.875rem',
                color: theme.colors.text.secondary,
                background: theme.colors.background.paper,
                padding: spacing.md,
                borderRadius: '8px',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.55,
                borderLeft: `3px solid ${theme.colors.primary[300]}`,
              }}
            >
              {quote.source_context}
            </div>
          )}
        </Stack>
      </div>
    </div>
  );
};
